/**
 * User registration service for handling backend API calls
 */

import API_CONFIG from "../apiConfig";

/**
 * Registers a user with the backend after authentication
 * @param userData User data to register
 * @returns Promise with the API response
 */
export const registerUser = async (userData: {
  emailId: string;
  name: string;
  profileUrl: string;
  userId: string;
}) => {
  try {
    const timestamp = new Date().toISOString();

    const response = await fetch(API_CONFIG.REGISTER_USER_URL, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        ...userData,
        timestamp,
      }),
    });

    if (!response.ok) {
      throw new Error(`Error registering user: ${response.status}`);
    }

    return await response.json();
  } catch (error) {
    console.error('User registration failed:', error);
    throw error;
  }
};

/**
 * Get the number of credits for a user
 * @param userId The user id to get credits for
 * @returns Promise with the user's credits information (float value)
 */
export const getUserCredits = async (userId: string): Promise<{ credits: number }> => {
  try {
    const response = await fetch(API_CONFIG.GET_USER_CREDITS_URL, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ userId }),
    });

    if (!response.ok) {
      throw new Error(`Error fetching user credits: ${response.status}`);
    }

    const data = await response.json();

    // Parse the credits from the new API response format
    // Example response: {"success":true,"message":"User credits found","credits":{"user":{"userId":"..."},"credits":5},"statusCode":200}
    if (data.success && data.credits && typeof data.credits.credits === 'number') {
      return { credits: data.credits.credits };
    }

    return { credits: 0 }; // Default to 0 if the API doesn't return in expected format
  } catch (error) {
    console.error('Failed to fetch user credits:', error);
    return { credits: 0 }; // Default to 0 on error
  }
};

export interface PaginatedResponse<T> {
  success: boolean;
  message: string;
  data: T[];
  statusCode: number;
  pagination: {
    currentPage: number;
    totalPages: number;
    totalItems: number;
    itemsPerPage: number;
    hasNextPage: boolean;
    hasPreviousPage: boolean;
  };
}

/**
 * Fetch past generations for a user
 * @param userId The user id to get past generations for
 * @returns Promise with the user's past generations
 */
export const getPastGenerations = async (
  userId: string,
  page: number = 1,
  limit: number = 12,
  type?: string,
  search?: string
): Promise<PaginatedResponse<any>> => {
  try {
    const requestBody = {
      userId,
      page,
      limit,
      type: type || 'all',
      ...(search && { search })
    };

    const response = await fetch(API_CONFIG.PAST_GENERATIONS_URL, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(requestBody)
    });

    if (!response.ok) {
      throw new Error('Failed to fetch past generations');
    }

    const result = await response.json();
    return result;
  } catch (error) {
    console.error('Error fetching past generations:', error);
    throw error;
  }
};

/**
 * Generate a payment link for upgrading to a plan
 * @param plan The plan to upgrade to ("Plus", "Pro", or "Topup")
 * @param userId The user's ID
 * @returns Promise with the payment link
 */
export const generatePaymentLink = async (plan: "Plus" | "Pro" | "Topup", userId: string, topupAmount: number): Promise<{ paymentLink: string }> => {
  try {
    const getAmountAndQuantity = (planType: string, amount: number) => {
      switch (planType) {
        case "Plus":
          return { amount: 0, quantity: 0 };
        case "Pro":
          return { amount: 0, quantity: 0 };
        case "Topup":
          return { amount: amount, quantity: 0 };
        default:
          return { amount: 0, quantity: 0 };
      }
    };

    const { amount, quantity } = getAmountAndQuantity(plan, topupAmount);

    const response = await fetch(API_CONFIG.GENERATE_PAYMENT_LINK_URL, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        service_type: plan.toLocaleLowerCase(),
        userId,
        amount,
        quantity,
      }),
    });



    if (!response.ok) {
      throw new Error(`Error generating payment link: ${response.status}`);
    }

    const data = await response.json();
    return { paymentLink: data.payment_link };
  } catch (error) {
    console.error('Failed to generate payment link:', error);
    throw error;
  }
};

/**
 * Generate a payment link for PhonePe (INR payments)
 * @param plan The plan to upgrade to ("Plus", "Pro", or "Topup")
 * @param userId The user's ID
 * @param topupAmount The topup amount (for Topup plan)
 * @returns Promise with the payment link
 */
export const generatePaymentLinkPhonePe = async (plan: "Plus" | "Pro" | "Topup", userId: string, topupAmount: number): Promise<{ paymentLink: string }> => {
  try {
    const getAmountAndQuantity = (planType: string, amount: number) => {
      switch (planType) {
        case "Plus":
          return { amount: 0, quantity: 0 };
        case "Pro":
          return { amount: 0, quantity: 0 };
        case "Topup":
          return { amount: amount, quantity: 0 };
        default:
          return { amount: 0, quantity: 0 };
      }
    };

    const { amount, quantity } = getAmountAndQuantity(plan, topupAmount);

    const response = await fetch(API_CONFIG.GENERATE_PAYMENT_LINK_PHONEPE_URL, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        service_type: plan.toLocaleLowerCase(),
        userId,
        amount,
        quantity,
      }),
    });

    if (!response.ok) {
      throw new Error(`Error generating PhonePe payment link: ${response.status}`);
    }

    const data = await response.json();
    return { paymentLink: data.payment_link };
  } catch (error) {
    console.error('Failed to generate PhonePe payment link:', error);
    throw error;
  }
};

/**
 * Generate a payment link with new service type format (e.g., "plus_monthly", "pro_4months")
 * @param serviceType The service type in new format (e.g., "plus_monthly", "pro_4months", "topup_yearly")
 * @param userId The user's ID
 * @param amount The payment amount
 * @returns Promise with the payment link
 */
export const generatePaymentLinkWithNewFormat = async (serviceType: string, userId: string, amount: number): Promise<{ paymentLink: string }> => {
  try {
    const response = await fetch(API_CONFIG.GENERATE_PAYMENT_LINK_URL, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        service_type: serviceType,
        userId,
        amount,
        quantity: 1,
      }),
    });

    if (!response.ok) {
      throw new Error(`Error generating payment link: ${response.status}`);
    }

    const data = await response.json();
    return { paymentLink: data.payment_link };
  } catch (error) {
    console.error('Failed to generate payment link:', error);
    throw error;
  }
};

/**
 * Generate a payment link for PhonePe with new service type format (INR payments)
 * @param serviceType The service type in new format (e.g., "plus_monthly", "pro_4months", "topup_yearly")
 * @param userId The user's ID
 * @param amount The payment amount in INR
 * @returns Promise with the payment link
 */
export const generatePaymentLinkPhonePeWithNewFormat = async (serviceType: string, userId: string, amount: number): Promise<{ paymentLink: string }> => {
  try {
    const response = await fetch(API_CONFIG.GENERATE_PAYMENT_LINK_PHONEPE_URL, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        service_type: serviceType,
        userId,
        amount,
        quantity: 1,
      }),
    });

    if (!response.ok) {
      throw new Error(`Error generating PhonePe payment link: ${response.status}`);
    }

    const data = await response.json();
    return { paymentLink: data.payment_link };
  } catch (error) {
    console.error('Failed to generate PhonePe payment link:', error);
    throw error;
  }
};

/**
 * Get the status of an order
 * @param orderId The order ID to check
 * @returns Promise with the order status
 */
export const getOrderStatus = async (orderId: string): Promise<{ status: string; message?: string }> => {
  try {
    const response = await fetch(API_CONFIG.ORDER_STATUS_URL, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ orderId }),
    });

    if (!response.ok) {
      throw new Error(`Error fetching order status: ${response.status}`);
    }

    const data = await response.json();
    return {
      status: data.status,
      message: data.message
    };
  } catch (error) {
    console.error('Failed to fetch order status:', error);
    throw error;
  }
};

/**
 * Get complete user profile details from the backend
 * @param userId The user id to get profile for
 * @returns Promise with the user's profile data
 */
export const getUserProfile = async (userId: string): Promise<{
  success: boolean;
  message: string;
  statusCode: number;
  data: {
    userId: string;
    emailId: string;
    name: string;
    profileUrl: string;
    companyName: string;
    phoneNumber: string;
    registrationDate: string;
    credits: number; // Float value
  };
}> => {
  try {
    const response = await fetch(API_CONFIG.GET_USER_PROFILE_URL, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ userId }),
    });

    if (!response.ok) {
      throw new Error(`Error fetching user profile: ${response.status}`);
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error('Failed to fetch user profile:', error);
    throw error;
  }
};

/**
 * Update user name in the backend
 * @param userId The user id to update name for
 * @param name The new name to set
 * @returns Promise with the API response
 */
export const updateUserName = async (userId: string, name: string): Promise<{
  success: boolean;
  message: string;
  statusCode: number;
  data: {
    userId: string;
    name: string;
    updatedAt: string;
  };
}> => {
  try {
    const response = await fetch(API_CONFIG.UPDATE_USER_NAME_URL, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ userId, name }),
    });

    if (!response.ok) {
      throw new Error(`Error updating user name: ${response.status}`);
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error('Failed to update user name:', error);
    throw error;
  }
};

/**
 * Update user profile fields using unified API endpoint
 * @param userId The user id to update profile for
 * @param updates Object containing fields to update (name, companyName, phoneNumber)
 * @returns Promise with the API response
 */
export const updateUserProfile = async (userId: string, updates: {
  name?: string;
  companyName?: string;
  phoneNumber?: string;
}): Promise<{
  success: boolean;
  message: string;
  statusCode: number;
  data?: any;
}> => {
  try {
    // Only include fields that have values (allowing empty strings to clear fields)
    const updateData = Object.entries(updates).reduce((acc, [key, value]) => {
      if (value !== undefined && value !== null) {
        acc[key] = value;
      }
      return acc;
    }, {} as any);

    const response = await fetch(API_CONFIG.UNIFIED_PROFILE_UPDATE_URL, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        userId,
        ...updateData
      }),
    });

    if (!response.ok) {
      throw new Error(`Error updating user profile: ${response.status}`);
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error('Failed to update user profile:', error);
    throw error;
  }
};

/**
 * Delete user account and all associated data
 * @param userId The user id to delete account for
 * @returns Promise with the API response
 */
export const deleteAccount = async (userId: string): Promise<{
  success: boolean;
  message: string;
  statusCode: number;
  data?: {
    userId: string;
    deletedAt: string;
    deletedCollections: string[];
  };
}> => {
  try {
    const response = await fetch(API_CONFIG.DELETE_ACCOUNT_URL, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ userId }),
    });

    if (!response.ok) {
      throw new Error(`Error deleting account: ${response.status}`);
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error('Failed to delete account:', error);
    throw error;
  }
}; 